import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Runtime settings. Every founder-configurable value lives in the SynthOS
// Admin panel (schema at .synth/admin/schema.json) and is read here from the
// project's own Supabase `global_settings` table. Unset keys fall back to the
// defaults below, so the product never hardcodes a configurable value and
// still works before any setting has been saved.
// ---------------------------------------------------------------------------

export type Settings = {
  announcement_enabled: boolean;
  announcement_text: string;
  hero_eyebrow: string;
  hero_headline: string;
  hero_subheadline: string;
  downloads_live: boolean;
  app_store_url: string;
  google_play_url: string;
  waitlist_enabled: boolean;
};

export const SETTINGS_DEFAULTS: Settings = {
  announcement_enabled: true,
  announcement_text:
    "FitBuddy is live on iOS & Android. Download free and start banking calories today.",
  hero_eyebrow: "The scale that pays you",
  hero_headline: "Lose weight. Bank calories.",
  hero_subheadline:
    "Every gram you lose earns real, spendable calories, so progress feels like permission, not punishment. Log your weight, watch your bank grow, and eat like you've earned it.",
  downloads_live: true,
  app_store_url: "",
  google_play_url: "",
  waitlist_enabled: false,
};

const KEYS = Object.keys(SETTINGS_DEFAULTS) as (keyof Settings)[];

/** Load settings from Supabase, merged over defaults. Never throws. */
export async function loadSettings(): Promise<Settings> {
  if (!supabase) return { ...SETTINGS_DEFAULTS };
  try {
    const { data, error } = await supabase
      .from("global_settings")
      .select("key, value")
      .in("key", KEYS as string[]);
    if (error || !data) return { ...SETTINGS_DEFAULTS };

    const merged: Settings = { ...SETTINGS_DEFAULTS };
    for (const row of data as { key: string; value: unknown }[]) {
      if (row.key in merged && row.value != null) {
        // value is a JSON scalar (string / boolean / number)
        (merged as Record<string, unknown>)[row.key] = row.value;
      }
    }
    return merged;
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
}
